import { MarketAssetUid } from "@silo/types";
import { Context, Data, HashMap, Option, pipe } from "effect";
import { MerklCampaign } from "./types";

/**
 * Essentially a lookup table for merkl campaigns.
 *
 * Get campaigns for a specific market asset.
 */
export class MerklCampaignMap {
  private hashMap: HashMap.HashMap<MarketAssetUid, MerklCampaign[]>;

  constructor() {
    this.hashMap = HashMap.empty();
  }

  public get(key: MarketAssetUid) {
    const { protocolKey, marketAddress, tokenAddress } = key;

    return HashMap.get(
      Data.struct({ protocolKey, marketAddress, tokenAddress })
    )(this.hashMap);
  }

  public getOrEmpty(key: MarketAssetUid) {
    return pipe(
      this.get(key),
      Option.getOrElse((): MerklCampaign[] => [])
    );
  }

  public set(key: MarketAssetUid, value: MerklCampaign[]) {
    const { protocolKey, marketAddress, tokenAddress } = key;

    const newHashMap = HashMap.set(
      Data.struct({ protocolKey, marketAddress, tokenAddress }),
      value
    )(this.hashMap);

    this.hashMap = newHashMap;
  }
}

export class MerklCampaignMapService extends Context.Tag(
  "MerklCampaignMapService"
)<MerklCampaignMapService, MerklCampaignMap>() {}

export function createMerklCampaignMap(
  merklCampaignArray: MerklCampaign[]
): MerklCampaignMap {
  const map = new MerklCampaignMap();

  for (const merklCampaign of merklCampaignArray) {
    const assetCampaigns = map.getOrEmpty(merklCampaign);

    map.set(merklCampaign, [...assetCampaigns, merklCampaign]);
  }

  return map;
}
